import { Expense } from "@finway-group/shared/lib/models"
import { MAX_INVOICE_NUMBER_LENGTH, isAllowedMultiLineCharsOnly } from "@finway-group/shared/lib/utils/validators"
import { Input } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useDebouncedCallback } from "use-debounce"
import { ExpenseService, NotificationService } from "~Shared/services"
import { NotificationTypeEnum } from "~Shared/services/notification.service"
import { isMileageExpense, isPerDiemExpense } from "~Shared/utils/expense.utils"
import { EXPENSE_PAGE_DEBOUNCE_DEFAULT_DELAY_MS } from "../../../../../shared/config/consts"
import { patchExpense } from "../../../../../shared/store/actions/expense/expenseActions"
import { useStateIfMounted } from "../../../../../shared/utils/hooks/useStateIfMounted"

interface InvoiceNumberFieldProps {
    expense: Expense
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    editable: boolean
    isLoading?: boolean
    required?: boolean
}

export const InvoiceNumberField = ({ expense, setIsExpenseUpdating, editable, isLoading, required }: InvoiceNumberFieldProps) => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const [invoiceNumber, setInvoiceNumber] = useStateIfMounted(expense.invoiceNumber)

    const invoiceNumberDebounceHandler = useDebouncedCallback(async (invoiceNumber) => {
        try {
            setIsExpenseUpdating?.(true)
            const hasDuplicate = await ExpenseService.checkHasDuplicate(expense, invoiceNumber)

            if (hasDuplicate) {
                setInvoiceNumber("")
                return
            }

            await patchExpense(expense.id, { invoiceNumber })(dispatch)
        } catch (err) {
            NotificationService.showErrorNotificationBasedOnResponseError(err, t("error:request.edit.title"))
        } finally {
            setIsExpenseUpdating?.(false)
        }
    }, EXPENSE_PAGE_DEBOUNCE_DEFAULT_DELAY_MS)

    if (!editable) {
        if (!expense?.invoiceNumber) {
            return null
        }
        return (
            <li>
                <span className="title">{isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_no") : t("label:invoice_no")}</span>
                <span data-testid="invoiceNumberExpenseForm" className="text">
                    {expense.invoiceNumber}
                </span>
            </li>
        )
    }

    const handleInvalidInput = (event: React.KeyboardEvent<HTMLInputElement> | React.ClipboardEvent<HTMLInputElement>) => {
        event.preventDefault()
        NotificationService.send(NotificationTypeEnum.ERROR, t("error:invoice_number.invalid_character.title"), t("error:invoice_number.invalid_character.message"))
        return false
    }

    return (
        <li>
            <span className={`w-1/2 title ${required && "label-required"}`}>
                {isMileageExpense(expense) || isPerDiemExpense(expense) ? t("label:internal_invoice_no") : t("label:invoice_no")}
            </span>
            <Input
                className="w-1/2"
                placeholder="Invoice number"
                value={invoiceNumber}
                onPaste={(event) => {
                    const clipboardData = event.clipboardData.getData("Text")
                    if (!isAllowedMultiLineCharsOnly(clipboardData)) {
                        return handleInvalidInput(event)
                    }
                    return true
                }}
                onKeyDown={(event) => {
                    if (!isAllowedMultiLineCharsOnly(event.key)) {
                        return handleInvalidInput(event)
                    }
                    return true
                }}
                onChange={(event) => {
                    const { value } = event.target
                    setInvoiceNumber(value)
                    invoiceNumberDebounceHandler.callback(value)
                    if (!value) {
                        invoiceNumberDebounceHandler.flush()
                    }
                }}
                onBlur={() => invoiceNumberDebounceHandler.flush()}
                disabled={isLoading}
                maxLength={MAX_INVOICE_NUMBER_LENGTH}
            />
        </li>
    )
}
